import React, { lazy, Suspense } from "react";
import {
  Navigate,
  Route,
  BrowserRouter as Router,
  Routes,
} from "react-router-dom"; // Added Navigate for redirection
import { AuthProvider } from "./context/AuthContext";
import LoadingComponent from "./components/LoadingComponent";
import "./index.css";
import PrivateRoute from "./routes/PrivateRoute";

const AdminLayout = lazy(() => import("./layouts/AdminLayout"));
const ClientIndex = lazy(() => import("./pages/Clients/Index"));
const NotificationPage = lazy(
  () => import("./pages/PushNotification/pushNotification")
);
const PaymentTransaction = lazy(() => import("./pages/Payment/index"));
const ArbitrageBatches = lazy(
  () => import("./pages/Arbitrage/ArbitrageBatches")
);
const TradingAssetIndex = lazy(() => import("./pages/Assets/Index"));
const AssetOrder = lazy(() => import("./pages/Assets/Order"));
const TradeblePairsIndex = lazy(() => import("./pages/TradablePairs/index"));
const NcwSupportedAssets = lazy(
  () => import("./pages/NcwSupportedAssets/Index")
);
const NcwSupportedBlockchain = lazy(
  () => import("./pages/NcwBlockChain/Index")
);
const BrokerApplications = lazy(
  () => import("./pages/Brokers/BrokerApplications")
);
const BrokerCommissions = lazy(
  () => import("./pages/Brokers/BrokerCommissions")
);
const BrokerOverview = lazy(() => import("./pages/Brokers/BrokerOverview"));
const BrokerTrades = lazy(() => import("./pages/Brokers/BrokerTrades"));
const ClientBalanceOverview = lazy(
  () => import("./pages/Clients/ClientBalanceOverview")
);
const ClientBankAccount = lazy(
  () => import("./pages/Clients/ClientBankAccounts")
);
const ClientUSDAccounts = lazy(
  () => import("./pages/Clients/ClientUSDAccounts")
);
const ClientUser = lazy(() => import("./pages/Clients/ClientUser"));
const ClientWithdrawallAddresses = lazy(
  () => import("./pages/Clients/ClientWithdrawallAddresses")
);
const ClientApiKeys = lazy(() => import("./pages/Clients/Tabs/Client-key-tab"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const CryptoDeposits = lazy(
  () => import("./pages/Deposits_Withdrawals/CryptoDeposits/Index")
);
const CryptoWithdrawals = lazy(
  () => import("./pages/Deposits_Withdrawals/Crypto-withdrawals")
);
const DepositLogs = lazy(
  () => import("./pages/Deposits_Withdrawals/deposit-logs")
);
const Deposits = lazy(() => import("./pages/Deposits_Withdrawals/Deposits"));
const WithdrawalLogs = lazy(
  () => import("./pages/Deposits_Withdrawals/withdrawal-logs")
);
const Withdrawals = lazy(
  () => import("./pages/Deposits_Withdrawals/withdrawals")
);
const TwoFactorAuth = lazy(() => import("./pages/Settings/Tabs/2FA"));
const AuditLogs = lazy(() => import("./pages/Extras/AuditLogs"));
const News = lazy(() => import("./pages/Extras/News"));
const SettingsAPIKeys = lazy(() => import("./pages/Extras/SettingsAPIKeys"));
const SettingsContact = lazy(() => import("./pages/Extras/SettingsContact"));
const SettingsSystem = lazy(() => import("./pages/Extras/SettingsSystem"));
const PoliciesPage = lazy(() => import("./pages/Policies"));
const InvestmentApplications = lazy(
  () => import("./pages/InvestmentApplications/Index")
);
const InvestmentProducts = lazy(
  () => import("./pages/InvestmentProducts/Index")
);
const UserSummary = lazy(() => import("./pages/Investments/UserSummary"));
const LiquidityPool = lazy(
  () => import("./pages/OTC/LiquidityPool/LiquidityPool")
);
const LPAlert = lazy(() => import("./pages/OTC/LiquidityPool/LPAlert"));
const LPHistory = lazy(() => import("./pages/OTC/LiquidityPool/LPHistory"));
const TradingFees = lazy(() => import("./pages/OTC/tradingFees/TradingFees"));
const TransactionHistory = lazy(
  () => import("./pages/OTC/transactionHistory/index")
);
const EditPolicy = lazy(() => import("./pages/Policies/EditPolicy"));
const ViewPolicy = lazy(() => import("./pages/Policies/ViewPolicy"));
const SettingPage = lazy(() => import("./pages/Settings/Settings"));
const UnauthorizedPage = lazy(() => import("./pages/Unauthorized"));
const EmailsTemplate = lazy(() => import("./pages/Emails/Emails"));
const VerifClients = lazy(() => import("./pages/Clients/VerifClients"));
const ChooseAsset = lazy(
  () => import("./pages/OTC/transactionHistory/AssetModal")
);
const AutoStackPurchases = lazy(() => import("./pages/AutoStack/Purchases"));
const UserAutoStacks = lazy(() => import("./pages/AutoStack/UserAutoStacks"));
const BanksIndex = lazy(() => import("./pages/Banks/Index"));
const BusinessRequests = lazy(
  () => import("./pages/Business/BusinessRequests")
);
const PinResetRequests = lazy(() => import("./pages/PinResetRequests/Index"));
const OrganisationsIndex = lazy(
  () => import("./pages/Clients/OrganisationsIndex")
);
const CustomOrders = lazy(() => import("./pages/OTC/Orders/Index"));
const DeferredSettlements = lazy(
  () => import("./pages/OTC/DeferredSettlements/Index")
);
const PayoutsIndex = lazy(() => import("./pages/Payouts/Index"));
const ManageTickets = lazy(() => import("./pages/Support/Tickets/Index"));
const ManageFaqs = lazy(() => import("./pages/Support/Faqs/Index"));
const AccessControlPage = lazy(() => import("./pages/AccessControl"));
const CompliancePage = lazy(() => import("./pages/Compliance"));
const LoginHistoryPage = lazy(() => import("./pages/LoginHistory"));
const ZAR8Conversions = lazy(() => import("./pages/ZAR8/index"));
const ZAR8Reserves = lazy(() => import("./pages/ZAR8/Reserves"));
const ZAR8Rewards = lazy(() => import("./pages/ZAR8/Rewards"));
const ZAR8YieldPositions = lazy(() => import("./pages/ZAR8/YieldPositions"));
const ZAR8YieldDistributions = lazy(
  () => import("./pages/ZAR8/YieldDistributions")
);
const ZAR8YieldConfiguration = lazy(
  () => import("./pages/ZAR8/YieldConfiguration")
);

const App = () => {
  return (
    <Router>
      <AuthProvider>
        <Suspense fallback={<LoadingComponent />}>
          <Routes>
            {/* Redirect root path ("/") to "/dashboard" */}
            {/*<Route path="/" element={<Navigate to="/admin/dashboard" />} />*/}

            <Route
              path="/"
              element={
                <PrivateRoute>
                  <Navigate to="/portal/dashboard" />
                </PrivateRoute>
              }
            />

            {/* Protected route for dashboard */}
            <Route
              path="/portal/dashboard"
              element={
                <PrivateRoute>
                  <Dashboard />
                </PrivateRoute>
              }
            />
            <Route
              path="/portal/clients"
              element={
                <PrivateRoute>
                  <ClientIndex />
                </PrivateRoute>
              }
            />
            <Route
              path="/portal/verif-clients"
              element={
                <PrivateRoute>
                  <VerifClients />
                </PrivateRoute>
              }
            />
            <Route
              path="/portal/organisations"
              element={
                <PrivateRoute>
                  <OrganisationsIndex />
                </PrivateRoute>
              }
            />
            <Route
              path="/portal/businesses"
              element={
                <PrivateRoute>
                  <BusinessRequests />
                </PrivateRoute>
              }
            />
            <Route
              path="/portal/pin-reset-requests"
              element={
                <PrivateRoute>
                  <PinResetRequests />
                </PrivateRoute>
              }
            />

            <Route
              path="/portal/notifications"
              element={
                <PrivateRoute>
                  <NotificationPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/portal/PaymentTransaction"
              element={
                <PrivateRoute>
                  <AdminLayout currentKey={"pay-sub1"} openKey="18">
                    <PaymentTransaction />
                  </AdminLayout>
                </PrivateRoute>
              }
            />
            <Route
              path="/portal/autostack-purchases"
              element={
                <PrivateRoute>
                  <AutoStackPurchases />
                </PrivateRoute>
              }
            />
            <Route
              path="/portal/user-autostacks"
              element={
                <PrivateRoute>
                  <UserAutoStacks />
                </PrivateRoute>
              }
            />
            <Route
              path="/portal/clientuser"
              element={
                <PrivateRoute>
                  <ClientUser />
                </PrivateRoute>
              }
            />
            <Route
              path="/portal/clientbalanceoverview"
              element={
                <PrivateRoute>
                  <ClientBalanceOverview />
                </PrivateRoute>
              }
            />
            <Route
              path="/portal/clientbankaccounts"
              element={
                <PrivateRoute>
                  <ClientBankAccount />
                </PrivateRoute>
              }
            />
            <Route
              path="/portal/client-usd-bankaccounts"
              element={
                <PrivateRoute>
                  <ClientUSDAccounts />
                </PrivateRoute>
              }
            />
            <Route
              path="/portal/client-withdrawall-addresses"
              element={
                <PrivateRoute>
                  <AdminLayout>
                    <ClientWithdrawallAddresses />
                  </AdminLayout>
                </PrivateRoute>
              }
            />
            <Route
              path="/portal/client-api-keys"
              element={
                <PrivateRoute>
                  <AdminLayout>
                    <ClientApiKeys />
                  </AdminLayout>
                </PrivateRoute>
              }
            />

            {/* Deposits Section */}

            <Route
              path="/portal/deposits"
              element={
                <PrivateRoute>
                  <Deposits />
                </PrivateRoute>
              }
            />
            <Route
              path="/portal/withdrawals"
              element={
                <PrivateRoute>
                  <Withdrawals />
                </PrivateRoute>
              }
            />
            <Route
              path="/portal/crypto-deposits"
              element={
                <PrivateRoute>
                  <CryptoDeposits />
                </PrivateRoute>
              }
            />
            <Route
              path="/portal/crypto-withdrawals"
              element={
                <PrivateRoute>
                  <CryptoWithdrawals />
                </PrivateRoute>
              }
            />
            <Route
              path="/portal/deposit-logs"
              element={
                <PrivateRoute>
                  <DepositLogs />
                </PrivateRoute>
              }
            />
            <Route
              path="/portal/withdrawal-logs"
              element={
                <PrivateRoute>
                  <AdminLayout currentKey={"dw-sub6"} openKey="5">
                    <WithdrawalLogs />
                  </AdminLayout>
                </PrivateRoute>
              }
            />

            {/* Arbitrage */}
            <Route
              path="/portal/arbitrage-batches"
              element={
                <PrivateRoute>
                  <AdminLayout>
                    <ArbitrageBatches />
                  </AdminLayout>
                </PrivateRoute>
              }
            />

            {/* Liquidity Pools */}
            <Route
              path="/portal/liquidity-pool"
              element={
                <PrivateRoute>
                  <LiquidityPool />
                </PrivateRoute>
              }
            />
            <Route
              path="/portal/lp-history"
              element={
                <PrivateRoute>
                  <LPHistory />
                </PrivateRoute>
              }
            />
            <Route
              path="/portal/lp-alert"
              element={
                <PrivateRoute>
                  <LPAlert />
                </PrivateRoute>
              }
            />
            <Route
              path="/portal/trading-fees"
              element={
                <PrivateRoute>
                  <AdminLayout>
                    <TradingFees />
                  </AdminLayout>
                </PrivateRoute>
              }
            />
            <Route
              path="/portal/transaction-history"
              element={
                <PrivateRoute>
                  <AdminLayout>
                    <TransactionHistory />
                  </AdminLayout>
                </PrivateRoute>
              }
            />
            <Route
              path="/portal/custom-orders"
              element={
                <PrivateRoute>
                  <AdminLayout currentKey={"otc-sub7"} openKey="6">
                    <CustomOrders />
                  </AdminLayout>
                </PrivateRoute>
              }
            />
            <Route
              path="/portal/deferred-settlements"
              element={
                <PrivateRoute>
                  <AdminLayout currentKey={"otc-sub8"} openKey="6">
                    <DeferredSettlements />
                  </AdminLayout>
                </PrivateRoute>
              }
            />
            <Route
              path="/portal/tradable-pairs"
              element={
                <PrivateRoute>
                  <AdminLayout currentKey={"otc-sub6"} openKey="6">
                    <TradeblePairsIndex />
                  </AdminLayout>
                </PrivateRoute>
              }
            />

            <Route
              path="/portal/chooseAsset"
              element={
                <PrivateRoute>
                  <AdminLayout>
                    <ChooseAsset />
                  </AdminLayout>
                </PrivateRoute>
              }
            />

            {/* Brokers Section */}
            <Route
              path="/portal/broker-applications"
              element={
                <PrivateRoute>
                  <BrokerApplications />
                </PrivateRoute>
              }
            />

            <Route
              path="/portal/broker-overview"
              element={
                <PrivateRoute>
                  <AdminLayout>
                    <BrokerOverview />
                  </AdminLayout>
                </PrivateRoute>
              }
            />
            <Route
              path="/portal/broker-trades"
              element={
                <PrivateRoute>
                  <AdminLayout>
                    <BrokerTrades />
                  </AdminLayout>
                </PrivateRoute>
              }
            />
            <Route
              path="/portal/broker-commissions"
              element={
                <PrivateRoute>
                  <BrokerCommissions />
                </PrivateRoute>
              }
            />

            {/* Investments Section */}
            <Route
              path="/portal/manage-investments"
              element={
                <PrivateRoute>
                  <InvestmentProducts />
                </PrivateRoute>
              }
            />
            <Route
              path="/portal/subscription-approval"
              element={
                <PrivateRoute>
                  <InvestmentApplications />
                </PrivateRoute>
              }
            />
            <Route
              path="/portal/user-summary"
              element={
                <PrivateRoute>
                  <UserSummary />
                </PrivateRoute>
              }
            />
            <Route
              path="/portal/payouts"
              element={
                <PrivateRoute>
                  <AdminLayout currentKey={"inv-sub4"} openKey="20">
                    <PayoutsIndex />
                  </AdminLayout>
                </PrivateRoute>
              }
            />

            {/* Assets Section */}

            <Route
              path="/portal/trading-assets"
              element={
                <PrivateRoute>
                  <TradingAssetIndex />
                </PrivateRoute>
              }
            />
            <Route
              path="/portal/asset-order"
              element={
                <PrivateRoute>
                  <AssetOrder />
                </PrivateRoute>
              }
            />
            <Route
              path="/portal/tradeble-pairs"
              element={
                <PrivateRoute>
                  <TradeblePairsIndex />
                </PrivateRoute>
              }
            />

            <Route
              path="/portal/ncw-supported-assets"
              element={
                <PrivateRoute>
                  <NcwSupportedAssets />
                </PrivateRoute>
              }
            />
            <Route
              path="/portal/ncw-supported-blockchain"
              element={
                <PrivateRoute>
                  <AdminLayout>
                    <NcwSupportedBlockchain />
                  </AdminLayout>
                </PrivateRoute>
              }
            />

            {/* Investments Section */}
            {/*<Route
            path="/admin/manage-investments"
            element={
              <PrivateRoute>
                <ManageInvestments />
              </PrivateRoute>
            }
          />*/}
            {/* News Section */}

            <Route
              path="/portal/all-news"
              element={
                <PrivateRoute>
                  <News />
                </PrivateRoute>
              }
            />

            {/* Settings Section */}

            <Route
              path="/portal/settings-system"
              element={
                <PrivateRoute>
                  <AdminLayout>
                    <SettingsSystem />
                  </AdminLayout>
                </PrivateRoute>
              }
            />

            <Route
              path="/portal/settings-contact"
              element={
                <PrivateRoute>
                  <AdminLayout>
                    <SettingsContact />
                  </AdminLayout>
                </PrivateRoute>
              }
            />
            <Route
              path="/portal/settings-api-keys"
              element={
                <PrivateRoute>
                  <AdminLayout>
                    <SettingsAPIKeys />
                  </AdminLayout>
                </PrivateRoute>
              }
            />

            {/* Emails */}

            <Route
              path="/portal/emails"
              element={
                <PrivateRoute>
                  <AdminLayout currentKey={"12"} openKey="12">
                    <EmailsTemplate />
                  </AdminLayout>
                </PrivateRoute>
              }
            />

            {/* Banks */}
            <Route
              path="/portal/banks"
              element={
                <PrivateRoute>
                  <BanksIndex />
                </PrivateRoute>
              }
            />

            {/* Support Center */}
            <Route
              path="/portal/manage-tickets"
              element={
                <PrivateRoute>
                  <AdminLayout currentKey={"support-sub1"} openKey="support">
                    <ManageTickets />
                  </AdminLayout>
                </PrivateRoute>
              }
            />
            <Route
              path="/portal/manage-faqs"
              element={
                <PrivateRoute>
                  <AdminLayout currentKey={"support-sub2"} openKey="support">
                    <ManageFaqs />
                  </AdminLayout>
                </PrivateRoute>
              }
            />

            {/* Emails */}

            {/* <Route
            path="/admin/emails"
            element={
              <PrivateRoute>
                <AdminLayout>
                  <Login />
                </AdminLayout>
              </PrivateRoute>
            }
          /> */}

            {/* Audit Logs */}
            <Route
              path="/portal/audit-logs"
              element={
                <PrivateRoute>
                  <AdminLayout currentKey={"17"} openKey="17">
                    <AuditLogs />
                  </AdminLayout>
                </PrivateRoute>
              }
            />

            {/* Policies Section */}

            <Route
              path="/portal/policies"
              element={
                <PrivateRoute>
                  <AdminLayout currentKey={"16"} openKey="16">
                    <PoliciesPage />
                  </AdminLayout>
                </PrivateRoute>
              }
            />

            {/* Two Factor Authentication */}

            <Route
              path="/portal/2fa"
              element={
                <PrivateRoute>
                  <AdminLayout currentKey={"18"} openKey="18">
                    <TwoFactorAuth />
                  </AdminLayout>
                </PrivateRoute>
              }
            />

            <Route
              path="/portal/settings"
              element={
                <PrivateRoute>
                  <AdminLayout currentKey={"settings-sub1"} openKey="13">
                    <SettingPage />
                  </AdminLayout>
                </PrivateRoute>
              }
            />
            <Route
              path="/portal/access-control"
              element={
                <PrivateRoute>
                  <AccessControlPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/portal/compliance"
              element={
                <PrivateRoute>
                  <AdminLayout currentKey={"compliance"} openKey="compliance">
                    <CompliancePage />
                  </AdminLayout>
                </PrivateRoute>
              }
            />
            <Route
              path="/portal/login-history"
              element={
                <PrivateRoute>
                  <LoginHistoryPage />
                </PrivateRoute>
              }
            />

            {/* ZAR8 */}
            <Route
              path="/portal/zar8-conversions"
              element={
                <PrivateRoute>
                  <ZAR8Conversions />
                </PrivateRoute>
              }
            />
            <Route
              path="/portal/zar8-reserves"
              element={
                <PrivateRoute>
                  <ZAR8Reserves />
                </PrivateRoute>
              }
            />
            <Route
              path="/portal/zar8-rewards"
              element={
                <PrivateRoute>
                  <ZAR8Rewards />
                </PrivateRoute>
              }
            />
            <Route
              path="/portal/zar8-yield-positions"
              element={
                <PrivateRoute>
                  <ZAR8YieldPositions />
                </PrivateRoute>
              }
            />
            <Route
              path="/portal/zar8-yield-distributions"
              element={
                <PrivateRoute>
                  <ZAR8YieldDistributions />
                </PrivateRoute>
              }
            />
            <Route
              path="/portal/zar8-yield-configuration"
              element={
                <PrivateRoute>
                  <ZAR8YieldConfiguration />
                </PrivateRoute>
              }
            />
            <Route path="/unauthorized" element={<UnauthorizedPage />} />

            <Route
              path="/portal/policies/view/:id"
              element={
                <PrivateRoute>
                  <AdminLayout currentKey={"16"} openKey="16">
                    <ViewPolicy />
                  </AdminLayout>
                </PrivateRoute>
              }
            />
            <Route
              path="/portal/policies/edit/:id"
              element={
                <PrivateRoute>
                  <AdminLayout currentKey={"16"} openKey="16">
                    <EditPolicy />
                  </AdminLayout>
                </PrivateRoute>
              }
            />
          </Routes>
        </Suspense>
      </AuthProvider>
    </Router>
  );
};

export default App;
